import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input
} from "@libs/zapi-ui/shadcn";
import { AppButton } from "@libs/zapi-ui/custom";
import { useState } from "react";
import { sendInvite } from "../services/sendInvite";
import { z } from "zod";
import { toast } from "sonner";
import { UserRoleSchema, AccessModeSchema } from "@libs/shared-permissions";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { getMemberRoles, getAccessModes } from "../const/const";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { AxiosError } from "axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { TAccessMode } from "../services/schemas";
interface InviteUserModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

export const getFormSchema = (t: TFunction<"settings">) =>
  z.object({
    role: UserRoleSchema.refine((val) => val, { message: t("invite.roleRequired") }),
    accessMode: AccessModeSchema,
    firstName: z
      .string()
      .min(1, { message: t("invite.firstNameRequired") })
      .min(2, { message: t("invite.firstNameTooShort") }),
    lastName: z
      .string()
      .min(1, { message: t("invite.lastNameRequired") })
      .min(2, { message: t("invite.lastNameTooShort") }),
    email: z.string({ message: t("invite.emailRequired") }).email({ message: t("invite.emailInvalid") })
  });

export function InviteUserModal({ isOpen, onOpenChange, organizationId, onSuccess }: InviteUserModalProps) {
  const { t } = useTranslation("settings");
  const { role: currentUserRole, accessMode: currentUserAccessMode } = useMeStore();
  const currentUserHasEditAccess = hasEditAccess(currentUserRole, currentUserAccessMode);

  const [formData, setFormData] = useState<{
    role: string;
    accessMode: string;
    firstName: string;
    lastName: string;
    email: string;
  }>({
    role: "",
    accessMode: currentUserHasEditAccess ? "edit" : "read",
    firstName: "",
    lastName: "",
    email: ""
  });

  function handleChange(field: keyof typeof formData, value: string) {
    const updatedData = { ...formData, [field]: value };
    if (field === "role" && value === "systemAdmin") {
      updatedData.accessMode = "edit";
    }
    setFormData(updatedData);
    setErrors({ ...errors, [field]: undefined });
  }

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    role?: string[];
    firstName?: string[];
    lastName?: string[];
    email?: string[];
  }>({});

  const handleClose = () => {
    setIsLoading(false);
    onOpenChange(false);
    setFormData({
      role: "",
      accessMode: currentUserHasEditAccess ? "edit" : "read",
      firstName: "",
      lastName: "",
      email: ""
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    const validatedFields = getFormSchema(t).safeParse(formData);
    if (!validatedFields.success) {
      setErrors(validatedFields.error.flatten().fieldErrors);
      toast.error(t("invite.invalidForm"));
      return;
    }

    const data = {
      orgId: organizationId.toString(),
      email: formData.email,
      role: formData.role,
      accessMode: formData.accessMode as TAccessMode,
      firstName: formData.firstName,
      lastName: formData.lastName
    };

    setIsLoading(true);
    const [, error] = await tryCatch<null, AxiosError<{ message: string }>>(sendInvite(data));
    handleClose();
    setIsLoading(false);
    if (error) {
      toast.error(error.response?.data.message || t("invite.sendFailed"));
      console.error("Error sending invite:", error);
      return;
    }

    onSuccess();
    toast.success(t("invite.sent"));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="font-medium">{t("invite.title")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="member-role">{t("invite.memberRole")}</Label>
            <Select value={formData.role} onValueChange={handleChange.bind(null, "role")}>
              <SelectTrigger id="member-role" className={errors.role ? "border-red-500" : ""}>
                <SelectValue placeholder={t("invite.selectOption")} />
              </SelectTrigger>
              <SelectContent>
                {getMemberRoles(t, currentUserRole).map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[14px] text-red-500 -mt-2">{errors.role?.[0]}</p>
          </div>

          {formData.role !== "systemAdmin" && (
            <div className="grid gap-2">
              <Label htmlFor="access-mode">{t("invite.accessMode")}</Label>
              <Select
                value={formData.accessMode}
                onValueChange={handleChange.bind(null, "accessMode")}
                disabled={!currentUserHasEditAccess}
              >
                <SelectTrigger id="access-mode">
                  <SelectValue placeholder={t("invite.selectAccessMode")} />
                </SelectTrigger>
                <SelectContent>
                  {getAccessModes(t).map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-4">
            <div className="flex flex-col gap-2 basis-1/2">
              <Label htmlFor="first-name">{t("invite.firstName")}</Label>
              <Input
                id="first-name"
                placeholder={t("invite.firstNamePlaceholder")}
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                inputError={errors.firstName?.[0]}
              />
            </div>
            <div className="flex flex-col gap-2 basis-1/2">
              <Label htmlFor="last-name">{t("invite.lastName")}</Label>
              <Input
                id="last-name"
                placeholder={t("invite.lastNamePlaceholder")}
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                inputError={errors.lastName?.[0]}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">{t("invite.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("invite.emailPlaceholder")}
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              inputError={errors.email?.[0]}
            />
          </div>
        </div>
        <DialogFooter className="!justify-start">
          <AppButton
            type="submit"
            onClick={handleSubmit}
            className="w-full sm:w-auto"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("invite.send")}
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
